/* ============================================================
   FGO HEADER — global behaviour
   ------------------------------------------------------------
   Split out of pdp-v2.js so the docking header works on every
   template. It used to bail immediately unless <body> carried
   the `pdp-v2` class, which only the product template sets.

   Owns three things: the measured promo-bar offset, the dock
   hysteresis, and the logo colour swap. Paired with
   fgo-header.css.
   ============================================================ */
/* ============================================================
   HEADER dock — hysteresis + measured promo height
   ------------------------------------------------------------
   The theme toggles .bg at a single threshold, so the header
   flickers when a scroll settles exactly on the boundary. The
   demo docks at one value and releases at a lower one, which
   cannot oscillate.

   Also measures the promo bar so the docked header sits under it
   rather than at a hardcoded offset.
   ============================================================ */
(function () {
  'use strict';

  var head = document.querySelector('#header');
  var promo = document.querySelector('[id*="theme-announcement"]');
  if (!head) return;

  /* --promo-h drives where the header sticks: top = --promo-h + 12 at rest,
     top = --promo-h when docked. Getting it wrong by writing 0 parks the
     header at the very top of the viewport, UNDER a promo bar that is
     z-index 101 — which is the bar sitting on top of the mark.

     This used to measure once, synchronously, at script execution, and then
     only again on resize. Run before the announcement bar has laid out,
     getBoundingClientRect().height returns 0, and 0 is what got written. The
     `var(--promo-h, 40px)` fallback in the CSS cannot save that: the property
     IS set, just set to zero, so the fallback never applies. The header then
     stayed under the bar for the life of the page unless you happened to
     resize the window, which is why it looked fine sometimes.

     Two guards. Never write a 0 while the element exists — remove the
     property instead, so the CSS fallback takes over. And keep measuring:
     on load, on font swap, and via ResizeObserver, since the bar is a
     rotating-message widget whose height can change after first paint. */
  var setPromoH = function () {
    if (!promo) {
      document.documentElement.style.setProperty('--promo-h', '0px');
      return;
    }
    var h = Math.round(promo.getBoundingClientRect().height);
    if (h > 0) document.documentElement.style.setProperty('--promo-h', h + 'px');
    else       document.documentElement.style.removeProperty('--promo-h');
  };
  setPromoH();
  addEventListener('resize', setPromoH, { passive: true });
  addEventListener('load', setPromoH);
  if (document.readyState === 'loading') {
    addEventListener('DOMContentLoaded', setPromoH);
  }
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(setPromoH).catch(function () {});
  }
  if (promo && window.ResizeObserver) {
    new ResizeObserver(setPromoH).observe(promo);
  }

  /* The theme's own handler (theme-header.js) decides docking from
     header.getBoundingClientRect().top <= 13. Our promo-bar offset
     (top: var(--promo-h)) makes that permanently false, so it never added
     .bg — and it strips the class on every scroll event, which defeated the
     cached `docked` flag this used to keep.

     It also drives the logo colour swap through .active on #headerLogo /
     #headerLogoAlt, so we cannot simply ignore it. Instead we state the full
     result unconditionally on every event, and register last (inside `load`,
     with a fresh function identity so it is a distinct listener) — listeners
     fire in registration order, so ours settles the class after the theme's. */

  var dark  = document.querySelector('#headerLogo');
  var light = document.querySelector('#headerLogoAlt');

  /* Two fixes for the flashing, and they are different problems.

     1. THE FLASH. The theme's handler strips .bg on every scroll event and we
        re-add it. Because the header transitions `background` over .25s, each
        strip/re-add restarted a cross-fade — which is the black-to-cream
        oscillation. Reading state off .bg made us complicit: the class we were
        reading was being cleared underneath us every frame.

        So our CSS now keys on `fgo-docked`, a class the theme has never heard
        of and therefore cannot remove. We still set .bg so the theme's own
        rules and anything else depending on it keep working, but nothing we
        style is driven by it. Our visual state changes only when the scroll
        state actually changes.

     2. THE BAND. 18/6 gave twelve pixels of hysteresis. One wheel notch is
        50-120px and momentum scrolling oscillates around a boundary, so the
        band was easy to straddle. 90/40 cannot be crossed by accident. */
  var DOCK_AT = 90, RELEASE_AT = 40;
  var docked = null;                 /* null = not yet applied */
  var ticking = false;

  var apply = function () {
    var y = window.scrollY;
    var next = docked ? y >= RELEASE_AT : y > DOCK_AT;

    /* .bg is kept in sync for the theme's benefit, cheaply and every time */
    if (head.classList.contains('bg') !== next) head.classList.toggle('bg', next);

    /* .active must ALSO be restated every event, not just on a state change.
       It is shared with the theme's handler, which rewrites it on every scroll
       event. Restating it only on transitions meant the theme won every event
       after the first — leaving the dark mark active on the black docked bar,
       i.e. an invisible logo. Same shared-ownership trap as .bg; toggle with
       an explicit force value is idempotent, so doing it every time is free. */
    if (dark  && dark.classList.contains('active')  === next)  dark.classList.toggle('active', !next);
    if (light && light.classList.contains('active') !== next) light.classList.toggle('active', next);

    if (next === docked) return;     /* geometry already correct */
    docked = next;
    head.classList.toggle('fgo-docked', next);
    /* Same state on <html>, because the element that actually carries the
       sticky offset is the Shopify section wrapper ABOVE #header, and a
       parent is not selectable from a child in CSS. See fgo-header.css. */
    document.documentElement.classList.toggle('fgo-head-docked', next);
  };

  /* rAF-throttled: scroll fires far faster than we can paint, and doing class
     work per event is what let the theme and this handler interleave mid-frame.

     A timer races the rAF because rAF starves in occluded windows, low-power
     mode and embedded panes — and a starved rAF here wedges `ticking` true
     forever, which kills docking for the life of the page. Whichever fires
     first wins; the loser no-ops. */
  var onScroll = function () {
    if (ticking) return;
    ticking = true;
    var done = false;
    var run = function () { if (done) return; done = true; ticking = false; apply(); direction(); };
    requestAnimationFrame(run);
    setTimeout(run, 120);
  };

  /* ---- DIRECTIONAL STICKY ------------------------------------------
     Hide on the way down, reveal on the way up — the reference's
     behaviour. Independent of docking: docking is a function of WHERE
     you are, this is a function of WHICH WAY you are going, and they
     compose (a revealed header at y=800 is still the docked variant).

     Three guards, each earning its place:
       DEADZONE  trackpads emit sub-pixel jitter in both directions;
                 without it the bar strobes when a finger rests still.
       REVEAL_AT above this the page is barely scrolled and hiding the
                 nav reads as a glitch rather than an intent.
       menu open never hide a bar the user is actively pointing at —
                 the mega panel is anchored to it and would fly away.  */
  /* 60, not 220. At 220 the bar survived two or three wheel notches, so it
     read as sticky-that-eventually-gives-up rather than as yielding to the
     reader. One notch is 50-120px, so 60 means the FIRST deliberate scroll
     down clears it — the reference's behaviour. Still above the noise floor,
     so a stray 10px nudge at the top of the page cannot trigger it. */
  var HIDE_BELOW = 60, DEADZONE = 6;
  var lastY = window.scrollY, hidden = false;

  var setHidden = function (want) {
    if (want === hidden) return;
    hidden = want;
    document.documentElement.classList.toggle('fgo-head-hidden', want);
  };

  var direction = function () {
    var y = window.scrollY;
    var dy = y - lastY;
    if (Math.abs(dy) < DEADZONE) return;
    lastY = y;

    /* the panel is positioned off the header; hiding it mid-hover
       would drag the menu the user is reading off the screen */
    var menuOpen = head.querySelector('#headerDrop') &&
                   head.matches(':hover');
    if (menuOpen) { setHidden(false); return; }

    if (y <= HIDE_BELOW) { setHidden(false); return; }
    setHidden(dy > 0);
  };

  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('load', function () {
    addEventListener('scroll', function () { onScroll(); }, { passive: true });
    apply();
  });
  apply();

  /* The top transition is gated behind this class (see fgo-header.css):
     first paint and the first measured correction must SNAP, not slide —
     a page should not open with its header animating into place. Double
     rAF = one fully painted frame with settled values; the timer covers
     contexts where rAF starves. Whichever fires first wins. */
  var readyOnce = false;
  var armReady = function () {
    if (readyOnce) return;
    readyOnce = true;
    document.documentElement.classList.add('fgo-head-ready');
  };
  requestAnimationFrame(function () { requestAnimationFrame(armReady); });
  setTimeout(armReady, 300);
})();

/* ==================================================================
   CINEMA HERO — the header dissolves over a full-bleed film.

   Only two things are needed beyond the CSS: a flag saying this page
   opens on a cinematic hero, and the header's real height, because the
   hero is pulled up by exactly that much so the FILM grows behind the
   bar while the copy inside stays put.

   Height is measured, never assumed: the header's resting and docked
   states differ, the promo bar may or may not be present, and a
   hardcoded value would leave a gap or a crop on some template.
   ================================================================== */
(function () {
  var hero = document.querySelector('[data-fgo-cinema-hero]');
  if (!hero) return;

  var head = document.getElementById('header');
  if (!head) return;

  var root = document.documentElement;

  var measure = function () {
    /* the RESTING height is what the hero must offset. Reading it while
       docked would pull the hero up by the wrong amount for the rest of
       the page's life, so skip the write whenever the bar is docked and
       keep the last good value. */
    if (head.classList.contains('fgo-docked')) return;
    /* Measure the WRAPPER, not the bar. #header carries 12px top and 24px
       bottom margin, so its own height (117) is ~36px short of the space it
       actually occupies (153) — and the hero was pulled up by the smaller
       number, leaving the film starting 73px below the top instead of
       running behind the bar. The Shopify section wrapper's box includes
       those margins, so it is the honest measurement. */
    /* The wrapper's box does NOT include the bar's margins — with no padding
       or border on the wrapper they collapse straight out of it, so reading
       the wrapper returned the same 117 as the bar itself. Add the margins
       explicitly; that is the space the hero has to climb over. */
    var cs = getComputedStyle(head);
    var h = Math.round(
      head.getBoundingClientRect().height +
      (parseFloat(cs.marginTop) || 0) +
      (parseFloat(cs.marginBottom) || 0)
    );
    if (h > 0) root.style.setProperty('--fgo-head-h', h + 'px');
  };

  measure();
  root.classList.add('fgo-cinema-hero');

  /* the mark scales and the pill has responsive padding, so the resting
     height is not constant across breakpoints */
  if ('ResizeObserver' in window) {
    new ResizeObserver(measure).observe(head);
  } else {
    window.addEventListener('resize', measure, { passive: true });
  }
  /* fonts land after first paint and change the bar's height with them */
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
})();

/* ==================================================================
   HEADER OFFSET — one measured value, every template.

   The sticky offset was a fixed 49px. That happens to equal today's
   promo bar (37px) plus the header's 12px gap, so it looks right —
   until the promo copy wraps to two lines, the bar is switched off, or
   a template renders without it. Each hero treatment then sits at a
   slightly different top spacing, which is the margin drift that shows
   up "depending on the header treatment".

   Measuring the promo bar and publishing it as a variable makes the
   offset correct by construction on all three heroes and at any
   viewport, instead of correct by coincidence at one.
   ================================================================== */
(function () {
  var root = document.documentElement;
  var promo = document.querySelector('[id*="announcement"]');
  var wrap  = document.querySelector('[id*="theme-header"]');
  if (!wrap) return;

  var apply = function () {
    var h = promo ? Math.round(promo.getBoundingClientRect().height) : 0;
    root.style.setProperty('--fgo-promo-h', h + 'px');
  };

  apply();
  if ('ResizeObserver' in window && promo) {
    new ResizeObserver(apply).observe(promo);
  } else {
    window.addEventListener('resize', apply, { passive: true });
  }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(apply);
}());

  /* ---- HERO OVERLAY PULL (homepage) --------------------------------
     The hero must begin at the promo bar's lower edge so the header
     card floats ON the photograph, as the reference does. The header's
     section wrapper sits between them in the DOM and occupies flow
     height, so the hero is pulled up by exactly the distance from the
     promo bar's bottom to the wrapper's bottom.

     MEASURED, not assumed: that distance is the wrapper's own height
     plus whatever gap precedes it, and both move with the promo bar's
     height, the header's margins and the viewport. A hardcoded value
     was 40px short at this breakpoint alone.

     Read at scrollY 0 only — once anything is stuck the rects no
     longer describe the resting layout. */
  (function () {
    var root = document.documentElement;
    var measurePull = function () {
      if (window.scrollY > 0) return;
      var wrap  = document.querySelector('[id*="theme-header"]');
      var promo = document.querySelector('[id*="theme-announcement"]');
      if (!wrap) return;
      var wb = wrap.getBoundingClientRect().bottom;
      var pb = promo ? promo.getBoundingClientRect().bottom : 0;
      var pull = Math.round(wb - pb);
      if (pull > 0) root.style.setProperty('--fgo-head-pull', pull + 'px');
    };
    measurePull();
    addEventListener('load', measurePull);
    addEventListener('resize', measurePull);
  })();

/* ==================================================================
   MENU INTENT — the hover state machine (22 Aug night).

   Pure CSS hover was twitchy in both directions on live: a cursor
   passing through SHOP's enlarged hit zone opened the panel with no
   intent, and a CSS close-delay can postpone a close but never CANCEL
   one — coming back mid-fade caught a half-faded panel. Two timers fix
   both: opening requires a 90ms dwell (a drive-by crosses the zone in
   30–80ms and never fires), and closing waits out a 420ms grace that
   re-entering the bar or the panel CANCELS outright — no fade ever
   starts, so there is nothing to flicker.

   The machine only engages on real hover hardware; touch keeps the
   drawer. CSS keys on #header.fgo-menu-open, with the old :has() hover
   rules gated behind html:not(.fgo-menu-js) as the no-JS fallback.
   ================================================================== */
(function () {
  var head = document.getElementById('header');
  var drop = document.getElementById('headerDrop');
  var shop = document.getElementById('headerShop');
  if (!head || !drop || !shop) return;
  if (!window.matchMedia || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  document.documentElement.classList.add('fgo-menu-js');

  var OPEN_DWELL = 90, CLOSE_GRACE = 420;
  var openT = null, closeT = null;
  var isOpen = function () { return head.classList.contains('fgo-menu-open'); };
  var setOpen = function (on) { head.classList.toggle('fgo-menu-open', on); };
  var hold = function () { if (closeT) { clearTimeout(closeT); closeT = null; } };
  var disarm = function () { if (openT) { clearTimeout(openT); openT = null; } };

  var wantOpen = function () {
    hold();
    if (isOpen() || openT) return;
    openT = setTimeout(function () { openT = null; setOpen(true); }, OPEN_DWELL);
  };
  var openNow = function () { hold(); disarm(); setOpen(true); };
  var wantClose = function () {
    disarm();
    hold();
    closeT = setTimeout(function () { closeT = null; setOpen(false); }, CLOSE_GRACE);
  };

  shop.addEventListener('pointerenter', wantOpen);
  shop.addEventListener('pointerleave', wantClose);
  /* the panel is only hoverable while open or fading — re-entry always
     re-commits, including the catch-it-mid-fade case */
  drop.addEventListener('pointerenter', openNow);
  drop.addEventListener('pointerleave', wantClose);
  /* wandering the bar (logo, icons) while open keeps it open; leaving
     the header entirely starts the grace */
  /* pointerMOVE, not just enter: leaving SHOP arms the close timer even
     when the cursor never exits the bar, and an armed timer needs
     continuous cancellation while the pointer wanders the header --
     enter alone missed the wander-to-icons path (measured: closed at
     grace end mid-bar). */
  head.addEventListener('pointermove', function () { if (isOpen()) hold(); });
  head.addEventListener('pointerenter', function () { if (isOpen()) hold(); });
  head.addEventListener('pointerleave', wantClose);
  drop.addEventListener('pointermove', function () { if (isOpen()) hold(); });

  /* 19 Sep 2026: the other top links (Subscribe & Save, Bundles, Recipes) are plain links with no menu of their
     own, so reaching one by pointer or keyboard closes an open Shop menu at once instead of waiting out the grace */
  Array.prototype.forEach.call(document.querySelectorAll('#headerInnerLeft .headerRightNavLink'), function (a) {
    var shut = function () { disarm(); hold(); setOpen(false); };
    a.addEventListener('pointerenter', shut);
    a.addEventListener('focus', shut);
  });

  /* keyboard parity: focusing SHOP opens, tabbing out closes, Esc closes */
  shop.addEventListener('focus', function () { setOpen(true); });
  head.addEventListener('focusout', function (e) {
    if (!head.contains(e.relatedTarget)) setOpen(false);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen()) setOpen(false);
  });
}());

/* OFF-SCREEN LOOPS OFF (19 Sep 2026). Marks every section that is well off screen with .is-offscreen, so the
   rule in theme-global.css can switch its decorative loops off. Why it lives here: a loop running anywhere on
   the page kept the header on a separate GPU layer, which drew the nav text soft until hovered. */
(function () {
  if (!('IntersectionObserver' in window)) return;
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { e.target.classList.toggle('is-offscreen', !e.isIntersecting); });
  }, { rootMargin: '150px 0px' });
  var watch = function () { document.querySelectorAll('.shopify-section').forEach(function (s) { io.observe(s); }); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch); else watch();
  document.addEventListener('shopify:section:load', function (e) { if (e.target && e.target.classList) io.observe(e.target); });
}());

/* ==================================================================
   NAV BEAD (19 Sep 2026): one indicator per nav group. See NAV BEAD in
   fgo-header.css for the look. This owns where it is and what shape:
     pressed / pointed at / keyboard-focused / SHOP's menu open
                                                      -> capsule there
     otherwise the page you are on (aria-current)    -> line there
     otherwise                                        -> folds and fades
   Hover follows a mouse only (pointerType 'mouse', the per-event form of
   (hover: hover) and (pointer: fine)), so a tablet tap can never leave a
   hover behind. A finger or pen gets press feedback instead: the capsule
   blooms in place under the finger (no glide from the current page) and
   lets go 160ms after the lift. Keyboard focus counts only when
   :focus-visible, and any press hands focus back to the pointer (ring off).
   Geometry is measured, snapped to device pixels, and re-measured when
   anything in the row resizes (fonts, breakpoints, docking).
   ================================================================== */
(function () {
  'use strict';
  var head = document.getElementById('header');
  if (!head || !window.matchMedia || !head.getBoundingClientRect) return;

  var desk = matchMedia('(min-width: 801px)');
  var still = matchMedia('(prefers-reduced-motion: reduce)');
  var LINE_H = 3, ICON_LINE = 14, CAPS_LIFT = .07, GRACE = 70, FADE = 90, RELEASE = 160;   /* capsule height and side padding come from CSS: --fgo-bead-h, --fgo-bead-pad */
  var now = function () { return window.performance && performance.now ? performance.now() : Date.now(); };
  var renders = [];

  var group = function (host, sel, pin) {
    if (!host) return;
    var list = Array.prototype.slice.call(host.querySelectorAll(sel));
    if (!list.length) return;
    var bead = document.createElement('span');
    bead.className = 'fgoNavBead';
    bead.setAttribute('aria-hidden', 'true');
    host.insertBefore(bead, host.firstChild);

    var hover = null, focus = null, press = null, hoverAt = 0, focusAt = 0;
    var last = null, lastBloom = false, lastKey = '', leaveT = 0, fadeT = 0, pressT = 0;
    var live = function (el) { return !!el && el.offsetWidth > 0 && list.indexOf(el) > -1; };
    var home = function () {
      for (var i = 0; i < list.length; i++) if (live(list[i]) && list[i].getAttribute('aria-current') === 'page') return list[i];
      return null;
    };

    var geom = function (el, bloom) {
      var dpr = window.devicePixelRatio || 1;
      var snap = function (v) { return Math.round(v * dpr) / dpr; };
      var hs = getComputedStyle(host);
      var CAP_H = parseFloat(hs.getPropertyValue('--fgo-bead-h')) || 34, PAD_X = parseFloat(hs.getPropertyValue('--fgo-bead-pad')) || 14;
      var hr = host.getBoundingClientRect(), r = el.getBoundingClientRect();
      var ox = hr.left + host.clientLeft, oy = hr.top + host.clientTop + host.clientHeight;
      var cx, cy, w;
      if (el.tagName === 'BUTTON') {                       /* icon: a circle on the glyph */
        var s = el.querySelector('svg'), sr = s ? s.getBoundingClientRect() : r;
        cx = sr.left + sr.width / 2; cy = sr.top + sr.height / 2;
        w = bloom ? CAP_H : ICON_LINE;
      } else {                                             /* word: caps sit ~.07em above the box centre */
        var cs = getComputedStyle(el), tw = r.width;
        /* tracking hangs one letter-space past the last letter; measure the ink, not the box (SHOP ends on its chevron) */
        var end = el.lastChild;
        while (end && end.nodeType === 3 && !end.nodeValue.trim()) end = end.previousSibling;
        if (end && end.nodeType === 3) tw -= parseFloat(cs.letterSpacing) || 0;
        cx = r.left + tw / 2;
        cy = r.top + r.height / 2 - CAPS_LIFT * (parseFloat(cs.fontSize) || 14);
        w = bloom ? tw + 2 * PAD_X : tw;
      }
      w = snap(w);
      var left = snap(cx - w / 2), bottom = snap(cy + CAP_H / 2);   /* line and capsule share one bottom edge */
      return { l: left - ox, w: w, b: oy - bottom, h: bloom ? CAP_H : LINE_H };
    };

    var set = function (el, bloom, on, ring, instant) {
      var g = el ? geom(el, bloom) : null;
      var key = (g ? [g.l, g.w, g.b, g.h].join(',') : lastKey.split('|')[0]) + '|' + bloom + on + ring;
      if (key === lastKey && !instant) return;
      lastKey = key;
      if (instant) bead.classList.add('is-snap');
      if (g) {
        bead.style.left = g.l + 'px'; bead.style.width = g.w + 'px';
        bead.style.bottom = g.b + 'px'; bead.style.height = g.h + 'px';
      }
      bead.classList.toggle('is-bloom', bloom);
      bead.classList.toggle('is-on', on);
      bead.classList.toggle('is-key', ring);
      if (instant) { void bead.offsetWidth; bead.classList.remove('is-snap'); }
    };

    var pick = function () {
      if (live(press)) return press;
      var h = live(hover) ? hover : null, f = live(focus) ? focus : null;
      if (h && f) return hoverAt >= focusAt ? h : f;
      return h || f || (pin && live(pin()) ? pin() : null);
    };

    /* inPlace: a finger went down. Never glide under it: the current page's
       line goes at once and the capsule blooms from a line at the finger. */
    var render = function (instant, inPlace) {
      if (!desk.matches) { clearTimeout(fadeT); fadeT = 0; set(null, false, false, false, true); last = null; return; }
      var t = pick(), el = t || home(), bloom = !!t, ring = !!t && t === focus;
      if (!el) {                                           /* nothing to mark: fold into a line where it is, then fade */
        clearTimeout(fadeT); fadeT = 0;
        if (live(last)) set(last, false, false, false, !!instant);
        else set(null, false, false, false, true);
        return;
      }
      var showing = parseFloat(getComputedStyle(bead).opacity) > .02;
      var jump = inPlace && (el !== last || !lastBloom);
      if (!showing || instant || jump) {                   /* appear AS the line at the target, then bloom from it */
        clearTimeout(fadeT); fadeT = 0;
        set(el, false, false, false, true);
        if (instant) { set(el, bloom, true, ring, true); last = el; lastBloom = bloom; return; }
      } else if (still.matches && (el !== last || bloom !== lastBloom)) {
        clearTimeout(fadeT);                               /* reduced motion: out, move, in */
        bead.classList.remove('is-on'); lastKey = '';
        fadeT = setTimeout(function () { fadeT = 0; set(el, false, false, false, true); set(el, bloom, true, ring, false); }, FADE);
        last = el; lastBloom = bloom;
        return;
      }
      if (fadeT) return;
      set(el, bloom, true, ring, false);
      last = el; lastBloom = bloom;
    };
    var letGo = function () {
      clearTimeout(pressT); pressT = 0;
      if (!press) return;
      pressT = setTimeout(function () { pressT = 0; press = null; render(); }, RELEASE);
    };
    renders.push({ render: render, reset: function () {
      clearTimeout(leaveT); clearTimeout(pressT); pressT = 0;
      hover = null; focus = null; press = null; bead.classList.remove('is-press'); render(true);
    } });

    list.forEach(function (el) {
      el.addEventListener('pointerenter', function (e) {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(leaveT); hover = el; hoverAt = now(); render();
      });
      el.addEventListener('pointerleave', function (e) {
        if (e.pointerType !== 'mouse') return;
        bead.classList.remove('is-press');
        clearTimeout(leaveT);
        leaveT = setTimeout(function () { if (hover === el) { hover = null; render(); } }, GRACE);
      });
      el.addEventListener('pointerdown', function (e) {
        focus = null;                                      /* a press hands focus back to the pointer: no ring */
        if (e.pointerType === 'mouse') {
          if (e.button === 0 && hover === el) bead.classList.add('is-press');
          render();
          return;
        }
        clearTimeout(pressT); pressT = 0;                  /* finger or pen: press feedback, in place */
        press = el; render(false, true);
      });
    });
    host.addEventListener('focusin', function (e) {
      if (list.indexOf(e.target) < 0) return;
      var vis = false;
      try { vis = e.target.matches(':focus-visible'); } catch (x) { vis = false; }
      focus = vis ? e.target : null; focusAt = now(); render();
    });
    host.addEventListener('focusout', function (e) {
      if (e.target !== focus || list.indexOf(e.relatedTarget) > -1) return;   /* focusin moves it */
      focus = null; render();
    });
    document.addEventListener('pointerup', function (e) {
      bead.classList.remove('is-press');
      if (e.pointerType !== 'mouse') letGo();
    }, true);
    document.addEventListener('pointercancel', function (e) { if (e.pointerType !== 'mouse') letGo(); }, true);
    window.addEventListener('blur', function () {
      clearTimeout(pressT); pressT = 0;
      hover = null; press = null; bead.classList.remove('is-press'); render();
    });

    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function () { render(true); });
      ro.observe(host); list.forEach(function (el) { ro.observe(el); });
    } else {
      window.addEventListener('resize', function () { render(true); }, { passive: true });
    }
    render(true);
  };

  document.documentElement.classList.add('fgo-bead-js');
  var shop = document.getElementById('headerShop');
  group(document.getElementById('headerInnerLeft'), '#headerInnerLeft > a', function () {
    return head.classList.contains('fgo-menu-open') ? shop : null;
  });
  group(document.getElementById('headerRight'), '#headerRightNav > a, #headerRightIcons > button');

  var all = function (instant) { renders.forEach(function (g) { g.render(instant); }); };
  /* SHOP's menu opening and closing (fgo-header.js MENU INTENT toggles the class). The theme also
     rewrites .bg on #header every scroll event, so only a change in the menu state is acted on. */
  var wasOpen = head.classList.contains('fgo-menu-open');
  if (window.MutationObserver) new MutationObserver(function () {
    var open = head.classList.contains('fgo-menu-open');
    if (open !== wasOpen) { wasOpen = open; all(false); }
  }).observe(head, { attributes: true, attributeFilter: ['class'] });
  var onDesk = function () { all(true); };
  if (desk.addEventListener) desk.addEventListener('change', onDesk); else if (desk.addListener) desk.addListener(onDesk);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(onDesk).catch(function () {});
  /* back/forward cache: the link you clicked still thinks it is hovered (or pressed) */
  window.addEventListener('pageshow', function (e) { if (e.persisted) renders.forEach(function (g) { g.reset(); }); });
}());

/* ==================================================================
   DRAWER BAND EXIT (23 Sep 2026). The drawer serves every width up to
   1079px now (fgo-header.css, TABLET BAND). Open it on an iPad in portrait,
   turn the iPad to landscape, and the viewport leaves the band: the drawer
   is display:none there but body.noScroll stays on, so the page will not
   scroll. Leaving the band shuts the drawer the theme's own way (its X).
   ================================================================== */
(function () {
  'use strict';
  if (!window.matchMedia) return;
  var wide = matchMedia('(min-width: 1080px)');
  var shut = function () {
    if (!wide.matches) return;
    var slider = document.getElementById('slider'), x = document.getElementById('headerClose');
    if (slider && x && slider.classList.contains('active')) x.click();
  };
  if (wide.addEventListener) wide.addEventListener('change', shut); else if (wide.addListener) wide.addListener(shut);
}());
